/**
 * The horizontal section rail under a project's title.
 *
 * Six destinations, from `PROJECT_SECTIONS` - the same definition the sidebar
 * uses, so the two can no longer disagree about what a project contains.
 *
 * **Why a scrolling rail rather than wrapping or a "more" menu.** Six labels
 * come to roughly 640px at the shipped type size. That fits on a 1920 canvas
 * with the 268px sidebar and fits at 1366 once the two longest labels use
 * their short forms. Below that it scrolls horizontally, which keeps every
 * section reachable; wrapping to a second line pushes the page content down by
 * a row that appears and disappears as the window resizes, and hiding the last
 * three behind "..." would bury the three newest parts of the product.
 *
 * The active item is scrolled into view on mount and on route change, because
 * a rail whose current position is off-screen tells the user nothing. That is
 * done with `scrollIntoView({ block: "nearest" })` on the horizontal axis only
 * - `block: "nearest"` is what stops the browser also scrolling the *page* to
 * bring the rail into view, which reads as a jump on every navigation.
 *
 * Keyboard behaviour is the platform's: these are links in a nav, so Tab
 * reaches them in order and Enter follows them. No roving tabindex and no
 * `role="tablist"` - these navigate to real routes rather than switching
 * panels in place, and calling them tabs would promise arrow-key behaviour
 * that would then have to be built and maintained.
 */

import { useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { PROJECT_SECTIONS } from "./projectNav";
import "./projects.css";

export function ProjectSectionRail({ projectId }: { projectId: string }) {
  const rail = useRef<HTMLElement>(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const active = rail.current?.querySelector<HTMLElement>(".section-rail__item--active");
    // Guarded because this is a convenience, not behaviour: the rail is fully
    // usable if nothing scrolls. `scrollIntoView` is absent in jsdom and in
    // some embedded engines, and an exception here would unmount the whole
    // project screen over a cosmetic nicety.
    if (typeof active?.scrollIntoView !== "function") return;
    // `inline: "nearest"` moves the rail only when the item is actually out of
    // view, so a rail that already fits never scrolls at all.
    active.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [pathname]);

  return (
    <nav className="section-rail" aria-label="Proje bölümleri" ref={rail}>
      <ul className="section-rail__list">
        {PROJECT_SECTIONS.map((section) => (
          <li key={section.key}>
            <NavLink
              to={section.path(projectId)}
              end={section.end}
              className={({ isActive }) =>
                isActive ? "section-rail__item section-rail__item--active" : "section-rail__item"
              }
            >
              {/*
                Both labels are rendered and CSS picks one. Swapping the text
                in JavaScript would need a resize listener and would make the
                accessible name change as the window moves; this way the full
                label is always the one in the DOM order the screen reader
                reads, and the short form is a purely visual substitution.
              */}
              <span className="section-rail__label">{section.label}</span>
              {section.shortLabel && (
                <span className="section-rail__label-short" aria-hidden="true">
                  {section.shortLabel}
                </span>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
